import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Interfaccia per i dati della scheda analisi da duplicare.
 * Contiene tutti i campi necessari per creare una copia di una scheda esistente.
 */
export interface SchedaDaDuplicare {
  /** ID originale della scheda (sarà null per il nuovo record) */
  id: number | null;
  /** Codice filiale della nuova scheda */
  codiceFiliale: string;
  /** Dati completi della scheda analisi specifica (schedaanalisi1, schedaanalisi2, etc.) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  datiScheda: any;
}

/**
 * Stato dello slice per la gestione dell'Analisi Visiva.
 * Gestisce principalmente il flusso di duplicazione delle schede.
 */
export interface AnalisiVisivaState {
  /**
   * Flag che indica se è in corso una duplicazione.
   * Quando true, il form della scheda leggerà i dati da schedaDaDuplicare
   * invece di caricarli dal server.
   */
  duplicazioneInCorso: boolean;

  /**
   * Dati della scheda da duplicare.
   * Popolato durante il processo di duplicazione e consumato dal form
   * della scheda specifica per pre-popolare i campi.
   */
  schedaDaDuplicare: SchedaDaDuplicare | null;
}

const initialState: AnalisiVisivaState = {
  duplicazioneInCorso: false,
  schedaDaDuplicare: null,
};

/**
 * Redux Slice per la gestione dello stato dell'Analisi Visiva.
 *
 * Questo slice è stato creato per sostituire l'uso di sessionStorage
 * nel flusso di duplicazione delle schede analisi, fornendo una
 * gestione dello stato più robusta e type-safe.
 *
 * @example
 * // Avviare una duplicazione
 * dispatch(setDuplicazione({
 *   id: null,
 *   codiceFiliale: 'ABC123',
 *   datiScheda: schedaOriginale
 * }));
 *
 * // Nel form della scheda, leggere i dati
 * const { duplicazioneInCorso, schedaDaDuplicare } = useSelector(state => state.analisiVisiva);
 * if (duplicazioneInCorso && schedaDaDuplicare) {
 *   // Usa schedaDaDuplicare.datiScheda per pre-popolare il form
 * }
 *
 * // Al termine, pulire lo stato
 * dispatch(clearDuplicazione());
 */
const analisiVisivaSlice = createSlice({
  name: 'analisiVisiva',
  initialState,
  reducers: {
    /**
     * Imposta i dati per la duplicazione di una scheda analisi.
     * Attiva il flag duplicazioneInCorso e memorizza i dati della scheda.
     *
     * @param state - Stato corrente
     * @param action - Payload contenente i dati della scheda da duplicare
     */
    setDuplicazione: (state, action: PayloadAction<SchedaDaDuplicare>) => {
      state.duplicazioneInCorso = true;
      state.schedaDaDuplicare = action.payload;
    },

    /**
     * Pulisce lo stato di duplicazione.
     * Da chiamare dopo che il form ha consumato i dati o in caso di annullamento.
     */
    clearDuplicazione: (state) => {
      state.duplicazioneInCorso = false;
      state.schedaDaDuplicare = null;
    },
  },
});

export const { setDuplicazione, clearDuplicazione } =
  analisiVisivaSlice.actions;

export default analisiVisivaSlice.reducer;
